import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PostResponse } from "../../entry/server/post.ts";
import { postRepo } from "../../repo/post-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";

export default function PostsList() {
  const [posts, setPosts] = useState<PostResponse[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { setMessage } = useContext(AppContext);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await postRepo.getPosts(page);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست پست‌ها ناموفق بود." });
        setPosts([]);
        return;
      }
      setPosts(response.data);
      setPagination(response.pagination ?? null);
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [page, setMessage]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  const onDelete = async (id: number) => {
    if (!window.confirm("آیا مطمئن هستید که می‌خواهید این پست را حذف کنید؟")) {
      return;
    }
    try {
      const response = await postRepo.deletePost(id);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف پست ناموفق بود." });
        return;
      }
      setMessage({ type: "success", message: "پست با موفقیت حذف شد." });
      void loadPosts();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AdminPageTitle title="پست‌ها" />
        <Link
          to="/admin/posts/new"
          className="rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400"
        >
          ایجاد پست
        </Link>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست پست‌ها...
        </div>
      ) : !posts.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          پستی یافت نشد.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عنوان</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نویسنده</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">وضعیت</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {posts.map((post) => (
                  <tr key={post.id} className="transition hover:bg-slate-900">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">{post.id}</td>
                    <td className="px-4 py-3 text-slate-100">{post.title}</td>
                    <td className="px-4 py-3 text-slate-300">{post.author || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          post.is_published
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-slate-700/60 text-slate-300"
                        }`}
                      >
                        {post.is_published ? "منتشر شده" : "پیش‌نویس"}
                      </span>
                    </td>
                    <td className="flex items-center gap-2 px-4 py-3">
                      <Link
                        to={`/admin/posts/${post.id}/edit`}
                        className="inline-flex rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 hover:text-indigo-300"
                      >
                        ویرایش
                      </Link>
                      <button
                        type="button"
                        onClick={() => onDelete(post.id)}
                        className="inline-flex rounded-lg border border-red-700 px-3 py-1.5 text-xs text-red-200 transition hover:border-red-400 hover:text-red-300"
                      >
                        حذف
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
